import { Flex } from '@bridebook/aura';
import { isDateExact } from '@bridebook/toolbox';
import {
  formatEventDate,
  formatEventTimeRange,
} from '@bridebook/web/lib/events/event-date-formatters';
import { sortEvents } from '@bridebook/web/lib/events/sort-unified-events';
import { isMainEvent, SimplifiedEventForDisplay } from '@bridebook/web/lib/events/types';
import {
  DATE_FORMAT,
  dateFromIDatePicker,
  format,
} from '@bridebook/web/lib/time-date-manipulation/time-date-manipulation';
import {
  useGetWeddingWebsiteDateFormat,
  WeddingWebsiteDateFormat,
} from '@bridebook/web/lib/wedding-website/hooks/use-get-wedding-website-date-format';
import { groupBy, partition } from 'lodash';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { SectionContainer } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/components/section-container';
import {
  BodyText,
  Header2,
  Header3,
} from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/components/typography';
import { useWeddingWebsiteData } from '@bridebook/web/components/wedding-website/view-mode/layouts-v2/data/data-context';
import { isScheduleSection } from '@bridebook/web/lib/wedding-website/utils/sections-typeguards';
import { BBOrCustomSupplier } from '@bridebook/web/lib/supplier/hooks/query/use-supplier';

type SimplifiedEventForDisplayWithSupplier = SimplifiedEventForDisplay & {
  supplier: BBOrCustomSupplier | undefined;
};

export const Schedule = () => {
  const { t } = useTranslation('weddingWebsite');
  const { events, wedding, website, mainSupplier, suppliers } = useWeddingWebsiteData();
  const section = website.sections.find(isScheduleSection);

  const [mainEventArr, restOfEventsArr] = partition(events, isMainEvent);
  const mainEvent = mainEventArr?.[0];

  // Transform events to include required data:
  // - Main Event: Add date from wedding data (if available) and supplier information
  // - Other Events: Add supplier data by matching supplier IDs with the suppliers array
  const simplifiedEvents: SimplifiedEventForDisplayWithSupplier[] = useMemo(() => {
    const mainEventWithAllDataArr: SimplifiedEventForDisplayWithSupplier[] = (() => {
      if (!wedding.date || !isDateExact(wedding.date) || !mainEvent) return [];
      const date = dateFromIDatePicker(wedding.date);
      if (!date) return [];
      return [
        {
          ...mainEvent,
          date: format(date, DATE_FORMAT.DATE.ISO),
          supplier: mainSupplier,
        },
      ];
    })();

    const restOfEventsWithSupplier: SimplifiedEventForDisplayWithSupplier[] = restOfEventsArr.map(
      (event) => ({
        ...event,
        supplier: suppliers.find((supplier) => supplier.id === event.supplier),
      }),
    );

    return sortEvents([...mainEventWithAllDataArr, ...restOfEventsWithSupplier]);
  }, [mainEvent, restOfEventsArr, wedding.date, mainSupplier, suppliers]);

  const groupedEvents = groupBy(
    simplifiedEvents.filter((evt) => evt.featured),
    (ev) => ev.date,
  );

  const dateFormat = useGetWeddingWebsiteDateFormat();

  if (!section || !section.visible) return null;
  return (
    <SectionContainer detailsSectionId="schedule" previewSectionId="schedule">
      <Header2>{t('previewMode.tile.schedule.title')}</Header2>
      <Flex className="flex-col gap-8">
        {Object.keys(groupedEvents).length === 0 && (
          <BodyText weight="subheader">{t('previewMode.tile.schedule.dateTBC')}</BodyText>
        )}
        {Object.entries(groupedEvents).map(([date, events]) => (
          <Flex className="flex-col gap-4" key={date}>
            <Header3>
              {formatEventDate(date, dateFormat === WeddingWebsiteDateFormat.Ordinal)}
            </Header3>
            {events.map((event, eventIndex) => (
              <Flex key={eventIndex} className="flex-col">
                <BodyText weight="subheader">{event.name}</BodyText>
                <BodyText>{formatEventTimeRangeWithSupplier(event)}</BodyText>
                <BodyText>{event.description}</BodyText>
              </Flex>
            ))}
          </Flex>
        ))}
      </Flex>
    </SectionContainer>
  );
};

const formatEventTimeRangeWithSupplier = (event: SimplifiedEventForDisplayWithSupplier) => {
  const timeRange = formatEventTimeRange(event.time, ' - ');
  return event.supplier?.name ? `${timeRange} ${event.supplier.name}` : timeRange;
};
